<template>
  <div class="header">
    <div class="header__left">
      <el-icon class="header__collapse" @click="appStore.toggleSidebar()">
        <component :is="appStore.sidebarCollapsed ? 'Expand' : 'Fold'" />
      </el-icon>
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/index' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item v-if="moduleTitle">{{ moduleTitle }}</el-breadcrumb-item>
        <el-breadcrumb-item v-if="pageTitle">{{ pageTitle }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="header__right">
      <!-- 告警统计：旧 index.html 的告警球 / 未读消息 -->
      <el-tooltip content="待处理告警" placement="bottom">
        <div class="header__badge" @click="router.push('/faultmanage/query')">
          <el-icon><WarningFilled /></el-icon>
          <span class="header__badge-num">{{ faultCount }}</span>
        </div>
      </el-tooltip>

      <el-tooltip content="未读消息" placement="bottom">
        <div class="header__badge" @click="router.push('/messagemanage/query')">
          <el-icon><Bell /></el-icon>
          <span class="header__badge-num">{{ unreadCount }}</span>
        </div>
      </el-tooltip>

      <el-divider direction="vertical" />

      <el-dropdown @command="onCommand">
        <div class="header__user">
          <el-avatar :size="30" :src="avatarUrl">
            {{ (userStore.userName || 'U').charAt(0) }}
          </el-avatar>
          <span class="header__user-name">{{ userStore.userName || '未登录' }}</span>
          <el-icon><ArrowDown /></el-icon>
        </div>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="portal">功能门户</el-dropdown-item>
            <el-dropdown-item command="contact">联系方式</el-dropdown-item>
            <el-dropdown-item command="password">修改密码</el-dropdown-item>
            <el-dropdown-item command="migration" divided>迁移进度看板</el-dropdown-item>
            <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useAppStore } from '@/store/app'
import { useUserStore } from '@/store/user'
import { API_BASE } from '@/settings'
import { getFaultAllCountApi, getUnReadCountApi } from '@/api/common'

/**
 * 顶栏 —— 对应旧 common.js header.js + index.html 的导航部分。
 * 告警/未读数量沿用旧轮询方式（旧 index.js: setInterval("getFault()", 100000)）。
 */

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const userStore = useUserStore()

const faultCount = ref(0)
const unreadCount = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

const moduleTitle = computed(() => {
  const seg = route.path.split('/').filter(Boolean)
  return seg.length > 1 ? seg[0] : ''
})
const pageTitle = computed(() => (route.meta?.title as string) || '')

const avatarUrl = computed(() => {
  const id = userStore.userInfo?.photoId
  return id ? `${API_BASE}/filedownload?fileId=${id}` : ''
})

async function refreshBadges() {
  try {
    faultCount.value = (await getFaultAllCountApi()) as any as number
  } catch {
    /* 静默失败，不打扰用户 */
  }
  try {
    unreadCount.value = (await getUnReadCountApi()) as any as number
  } catch {
    /* 静默失败 */
  }
}

async function onCommand(cmd: string) {
  if (cmd === 'logout') {
    await ElMessageBox.confirm('您确定要退出吗?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    await userStore.logout()
    router.push('/login')
    return
  }
  if (cmd === 'portal') return router.push('/index')
  if (cmd === 'migration') return router.push('/migration')
  if (cmd === 'contact') {
    ElMessage.info(
      `手机：${userStore.userInfo?.mobile || '-'}　邮箱：${userStore.userInfo?.email || '-'}`
    )
    return
  }
  if (cmd === 'password') {
    router.push('/usermanage/changePwd')
  }
}

onMounted(() => {
  refreshBadges()
  timer = setInterval(refreshBadges, 100000)
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})
</script>

<style scoped lang="scss">
.header {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  &__collapse {
    font-size: 18px;
    cursor: pointer;
    color: $c-text-regular;

    &:hover {
      color: $c-primary;
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__badge {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: $c-danger;
    font-size: 16px;

    &-num {
      font-size: 13px;
      font-weight: 600;
    }
  }

  &__user {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    outline: none;

    &-name {
      font-size: 13px;
      color: $c-text-regular;
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
